import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Briefcase,
  DownloadSimple,
  PaperPlaneTilt,
  UserPlus,
  Tag,
  Clock,
  Pulse,
  Shuffle,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_CONFIG = {
  "job.created":       { icon: Briefcase,       color: "text-[#006994]",  bg: "bg-[#006994]/10", label: "created job" },
  "export.created":    { icon: DownloadSimple,  color: "text-emerald-400", bg: "bg-emerald-500/10", label: "exported" },
  "instantly.push":    { icon: PaperPlaneTilt,  color: "text-orange-400",  bg: "bg-orange-500/10", label: "pushed to Instantly" },
  "user.created":      { icon: UserPlus,        color: "text-[#0EA5E9]",   bg: "bg-[#0EA5E9]/10", label: "joined the team" },
  "tag.created":       { icon: Tag,             color: "text-purple-400",  bg: "bg-purple-500/10", label: "created tag" },
};

const FILTERS = [
  { value: "",                  label: "All activity" },
  { value: "job.created",       label: "Jobs" },
  { value: "export.created",    label: "Exports" },
  { value: "instantly.push",    label: "Instantly pushes" },
  { value: "user.created",      label: "Users" },
  { value: "tag.created",       label: "Tags" },
];

const relativeTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const ActivityPage = () => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/activity?limit=500`, { headers: getAuthHeaders() });
        setEvents(res.data?.events || []);
      } catch {
        // ignore — non-super-admins get 403
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    let list = events;
    if (filter) list = list.filter(e => e.action === filter);
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      list = list.filter(e =>
        (e.user_name || "").toLowerCase().includes(q) ||
        (e.user_email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [events, filter, userSearch]);

  const countByAction = useMemo(() => {
    const c = {};
    for (const e of events) c[e.action] = (c[e.action] || 0) + 1;
    return c;
  }, [events]);

  if (!isSuperAdmin) {
    return (
      <div className="space-y-6" data-testid="activity-page-forbidden">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Activity History
          </h1>
        </div>
        <Card className="glass-card border-slate-200">
          <CardContent className="p-10 text-center">
            <Shuffle size={48} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-700 font-medium">Super admin access only</p>
            <p className="text-slate-500 text-sm mt-1">
              Activity across all users is restricted. Contact <span className="text-[#006994]">info@dolphin-digital.com</span>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Activity History
          </h1>
          <p className="text-slate-500 mt-1">
            Timeline of every action taken across the platform
          </p>
        </div>
        <input
          type="text"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search by user name or email..."
          className="px-3 py-2 rounded-md bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#006994] w-72"
          data-testid="activity-user-search"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(f => {
          const count = f.value === "" ? events.length : (countByAction[f.value] || 0);
          const active = filter === f.value;
          return (
            <button
              key={f.value || "all"}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                active
                  ? "bg-[#006994]/20 border-[#006994]/50 text-[#006994]"
                  : "bg-white/60 border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-200"
              }`}
              data-testid={`activity-filter-${f.value || "all"}`}
            >
              {f.label} <span className="opacity-60 ml-1">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="glass-card border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
              <Pulse size={18} className="text-[#006994]" />
              Timeline
              <span className="text-xs text-slate-500 font-normal">
                ({visible.length} {visible.length === 1 ? "event" : "events"})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-12">
                <Pulse size={48} className="mx-auto text-slate-400 mb-3" />
                <p className="text-slate-500">No activity matches your filters</p>
              </div>
            ) : (
              <div className="space-y-1" data-testid="activity-timeline">
                {visible.map(ev => {
                  const cfg = ACTION_CONFIG[ev.action] || { icon: Pulse, color: "text-slate-500", bg: "bg-slate-100", label: ev.action };
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={ev.id}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-white/60 transition-colors"
                      data-testid={`activity-event-${ev.id}`}
                    >
                      <div className={`p-2 rounded-md flex-shrink-0 ${cfg.bg}`}>
                        <Icon size={14} className={cfg.color} weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 leading-tight">
                          <span className="font-semibold text-slate-900">{ev.user_name}</span>
                          <span className="text-slate-500"> {cfg.label} </span>
                          <span className="font-medium text-slate-800">"{ev.resource_name}"</span>
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {ev.action === "job.created" && ev.metadata && (
                            <>
                              <span className="text-[11px] text-slate-500">{ev.metadata.status}</span>
                              {ev.metadata.cleaned_leads > 0 && (
                                <span className="text-[11px] text-slate-500">· {ev.metadata.cleaned_leads.toLocaleString()} cleaned</span>
                              )}
                            </>
                          )}
                          {ev.action === "export.created" && ev.metadata && (
                            <span className="text-[11px] text-slate-500">
                              {(ev.metadata.format || "").toUpperCase()} · {ev.metadata.leads_count?.toLocaleString() || 0} leads
                            </span>
                          )}
                          {ev.action === "instantly.push" && ev.metadata && (
                            <>
                              <span className="text-[11px] text-slate-500">{ev.metadata.row_count?.toLocaleString() || 0} leads</span>
                              {ev.metadata.status && (
                                <span className={`text-[11px] px-1.5 rounded ${
                                  ev.metadata.status === "success" ? "bg-emerald-500/10 text-emerald-400" :
                                  ev.metadata.status === "processing" ? "bg-blue-500/10 text-blue-400" :
                                  ev.metadata.status === "failed" ? "bg-red-500/10 text-red-400" :
                                  "bg-yellow-500/10 text-yellow-400"
                                }`}>{ev.metadata.status}</span>
                              )}
                              {ev.metadata.ghl_synced > 0 && (
                                <span className="text-[11px] text-[#0EA5E9]">· {ev.metadata.ghl_synced} GHL synced</span>
                              )}
                            </>
                          )}
                          {ev.action === "user.created" && ev.metadata && (
                            <span className="text-[11px] text-slate-500">{ev.metadata.role} · {ev.metadata.email}</span>
                          )}
                          {ev.action === "tag.created" && ev.metadata && (
                            <span className="text-[11px] text-slate-500">{ev.metadata.category_count} categories</span>
                          )}
                          {ev.user_email && (
                            <span className="text-[11px] text-slate-400">· {ev.user_email}</span>
                          )}
                        </div>
                      </div>
                      <span className="flex items-center gap-1 text-[11px] text-slate-500 flex-shrink-0" title={ev.timestamp}>
                        <Clock size={11} />
                        {relativeTime(ev.timestamp)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ActivityPage;
